import { CompassIcon, HomeIcon, Loader2Icon, RefreshCwIcon, SparklesIcon, TriangleAlertIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { Button } from '@/components/ui/button'
import { canRecover, failedAssetUrl, isChunkLoadError, recover, reloadNow } from '@/lib/app-version'
import { cn } from '@/lib/utils'

const ICONS = { stale: SparklesIcon, notFound: CompassIcon, failed: TriangleAlertIcon }

/**
 * Route error screen. A page whose code is gone after a deploy reloads straight into that page on the new
 * version (at most twice a minute, so it never loops); anything else gets a calm card with a way out.
 * `inShell` keeps it inside the app's content panel so the sidebar stays.
 */
export function RouteError({ inShell = false }: { inShell?: boolean }) {
  const { t } = useTranslation()
  const error = useRouteError()
  const stale = isChunkLoadError(error)
  const [reloading] = useState(() => stale && canRecover())

  useEffect(() => {
    if (reloading) void recover(failedAssetUrl(error))
  }, [reloading, error])

  const frame = cn('grid place-items-center px-4 py-10', inShell ? 'min-h-[60vh]' : 'min-h-dvh bg-sidebar')

  if (reloading) {
    return (
      <main className={frame} aria-busy="true">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.3 }}
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Loader2Icon className="size-4 animate-spin" />
          {t('update.updating')}
        </motion.p>
      </main>
    )
  }

  const kind = stale ? 'stale' : isRouteErrorResponse(error) && error.status === 404 ? 'notFound' : 'failed'
  const Icon = ICONS[kind]

  return (
    <main className={frame}>
      <motion.section
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="surface-card w-full max-w-sm p-8 text-center"
      >
        <div className="mx-auto grid size-12 place-items-center rounded-full bg-primary/10 text-primary">
          <Icon className="size-5" />
        </div>
        <h1 className="mt-5 text-lg font-semibold tracking-tight">{t(`update.${kind}.title`)}</h1>
        <p className="mt-1.5 text-sm text-balance text-muted-foreground">{t(`update.${kind}.body`)}</p>
        <div className="mt-6 grid gap-2">
          {kind !== 'notFound' && (
            <Button onClick={() => void reloadNow(failedAssetUrl(error))}>
              <RefreshCwIcon />
              {t('update.reload')}
            </Button>
          )}
          <Button variant={kind === 'notFound' ? 'default' : 'ghost'} asChild>
            <a href="/">
              <HomeIcon />
              {t('update.home')}
            </a>
          </Button>
        </div>
        {import.meta.env.DEV && kind === 'failed' && (
          <pre className="mt-6 max-h-40 overflow-auto rounded-lg bg-muted p-3 text-start text-xs" dir="ltr">
            {error instanceof Error ? error.stack : JSON.stringify(error, null, 2)}
          </pre>
        )}
      </motion.section>
    </main>
  )
}
